import type { FileImportRow } from '@/types/banking'

/**
 * Stalled-import detection for `file_imports` rows.
 *
 * A row is written as `pending` by the browser, flipped to `processing` by the
 * Trigger.dev task, and to `completed` / `failed` by the same task when it
 * finishes. Nothing else ever writes it — so if the task never starts, or is
 * killed mid-run, the row keeps a non-terminal status forever. Two consequences,
 * both reported in the DCP-298 review:
 *
 *  - `useFileImports` polls every 2s for as long as any row is non-terminal, so
 *    one dead row makes every visitor of the page issue a request every 2s,
 *    indefinitely;
 *  - the row sits in Import History looking like work in progress, spinner and
 *    all, which is the same "something is happening" lie the ticket is about.
 *
 * Both need the same missing fact: *is this row still plausibly alive?* This
 * module answers it from the row's own timestamp, which needs no extra query and
 * no server-side write.
 *
 * Deliberately NOT done here: marking such rows `failed` in the database. That
 * claims the import failed, which we do not know — only that nothing reported
 * back. It would also only ever cover rows somebody was watching in a live tab;
 * a row orphaned by a task that died while the page was closed would keep
 * polling forever. Reaping rows for real belongs server-side, where every row is
 * visible; tracked separately.
 */

/** Statuses the importer may still move away from. */
const NON_TERMINAL_STATUSES: readonly string[] = ['pending', 'processing']

/**
 * Longest a single import run can possibly live.
 *
 * Mirrors `maxDuration: 600` in `packages/tasks/trigger.config.ts` — Trigger.dev
 * kills any run at that point, so no import can still be executing past it.
 * Every "is this normal?" judgement about a running import derives from this
 * number rather than from a guess about how slow a big file might be.
 *
 * Keep in step with `trigger.config.ts`; raising the platform ceiling without
 * raising this would make the UI call live imports stalled.
 */
export const IMPORT_RUN_CEILING_MS = 10 * 60 * 1000

/**
 * How long a non-terminal row may go untouched before it is treated as stalled.
 *
 * {@link IMPORT_RUN_CEILING_MS} plus 5 minutes of slack, which covers time spent
 * queued before the run starts (queueing does not touch the row) and modest
 * client clock skew.
 *
 * The margin matters in one direction only: too low would label a running import
 * as stalled and stop the polling that would have picked up its result, which is
 * worse than a phantom row lingering a few minutes longer.
 */
export const STALLED_IMPORT_MS = IMPORT_RUN_CEILING_MS + 5 * 60 * 1000

/** Row fields this module needs — keeps the helpers usable on partial rows. */
type StallCandidate = Pick<FileImportRow, 'status'> &
  Partial<Pick<FileImportRow, 'created_at' | 'updated_at'>>

/**
 * Whether the importer may still change this row's status.
 * @param status - Status string straight off the row
 */
export function isImportNonTerminal(status: string): boolean {
  return NON_TERMINAL_STATUSES.includes(status)
}

/**
 * Whether a non-terminal row has gone quiet for longer than
 * {@link STALLED_IMPORT_MS}.
 *
 * Reads `updated_at` — bumped by the tenant `update_updated_at` trigger, so it
 * marks the last time the importer actually touched the row — and falls back to
 * `created_at` for payloads that predate it. An unparsable or missing timestamp
 * returns `false`: without a timestamp we cannot claim anything, and the safe
 * default is to keep treating the row as live.
 *
 * @param imp - File import row (only status and timestamps are read)
 * @param now - Current epoch ms; injectable for tests
 * @returns `true` only for a non-terminal row that is provably past any live run
 */
export function isImportStalled(imp: StallCandidate, now: number = Date.now()): boolean {
  if (!isImportNonTerminal(imp.status)) return false

  const lastActivity = Date.parse(imp.updated_at ?? imp.created_at ?? '')
  if (Number.isNaN(lastActivity)) return false

  // A negative age means the client clock is behind the server's. Treated as
  // live for the same reason as a missing timestamp — it is not evidence of a
  // stall, and skew large enough to matter would otherwise silently stop the
  // poller on healthy imports.
  return now - lastActivity > STALLED_IMPORT_MS
}

/**
 * Whether this row is worth polling for: still non-terminal, not yet stalled.
 *
 * The single source of truth for both pollers — the Import History list
 * (`useFileImports`) and the watched-row status poller (`useFileImportStatus`).
 * Sharing it is the point: the two ran on different conditions before, so one
 * kept firing every 2s after the other had given up, and the same row could be
 * labelled stalled by the UI while a request for it was still in flight.
 *
 * Note the deliberate consequence: once this returns `false` the row stops being
 * refreshed, so a run that was queued past the cutoff and then completed will
 * keep showing its last known state until something else refetches (the Refresh
 * button in Import History, or a remount). That is the accepted cost of not
 * polling forever — the alternative was one dead row issuing requests every 2s
 * for every visitor, indefinitely (DCP-298 review).
 *
 * @param imp - File import row (only status and timestamps are read)
 * @param now - Current epoch ms; injectable for tests
 */
export function isImportLive(imp: StallCandidate, now: number = Date.now()): boolean {
  return isImportNonTerminal(imp.status) && !isImportStalled(imp, now)
}

/**
 * Whether the file-import list should keep polling.
 *
 * Extracted from the query hook so the poll decision is directly testable and
 * cannot drift from what the UI labels stalled: exactly the rows that keep the
 * poller alive are the rows still rendered as in-progress.
 *
 * @param rows - Current page of file import rows, if loaded
 * @param now - Current epoch ms; injectable for tests
 * @returns `true` while at least one row is non-terminal and not yet stalled
 */
export function shouldPollFileImports(
  rows: readonly StallCandidate[] | undefined,
  now: number = Date.now(),
): boolean {
  return rows?.some((imp) => isImportLive(imp, now)) ?? false
}
